/**
 * OracleSkillEffect — Astral oracle-path awareness window.
 *
 * Oracle pops a 5-second window of precognitive awareness. During the window:
 * 1. All enemy attack arcs render as persistent overlays (via ForesightRenderSystem).
 * 2. Patrol routes and aggro states become visible (Oracle overlay).
 * 3. The player's next Phase Step during the window costs no cooldown (one free teleport).
 *
 * Upgrade hooks:
 * - oracle_duration: `durationMs` (additive, scope: oracle) — extends window by 2000ms.
 * - oracle_phase_step: `phaseStepInvulnerabilityMs` (additive, scope: oracle) — boosts Phase
 *   Step's invuln window from 250ms to 500ms while Oracle is active.
 *
 * Free Phase Step: `consumeFreePhaseStep()` is called by PhaseStepSkillEffect.activate()
 * before starting the cooldown. Returns true once per Oracle window (consumes the flag);
 * returns false if already used or Oracle has expired.
 *
 * Invuln override: when oracle_phase_step is unlocked, Oracle sets
 * `AstralTeleportSystem.setInvulnOverride(500)` on activation and restores the Phase Step
 * base (250ms) on deactivation. This is finalized in oracle_phase_step's task — Oracle
 * only applies it when the resolver reports the bonus.
 *
 * Phase sequence: active window (5000ms, extendable) → medium cooldown (1200ms).
 *
 * Prescience (replacesId: oracle) inherits this class's active-window behavior and
 * adds a passive death-save layer. This class is therefore not marked `final`.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § oracle
 * @see ADR-064 (attunement shared systems architecture)
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';
import type { ForesightRenderSystem } from '../../../systems/ForesightRenderSystem.js';
import type { AstralTeleportSystem } from '../../../systems/AstralTeleportSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface OracleDeps {
  readonly renderer: Pick<ForesightRenderSystem, 'showOracleOverlay' | 'hideOracleOverlay'>;
  readonly teleport: Pick<AstralTeleportSystem, 'setInvulnOverride'>;
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  /** All current enemies — passed to showOracleOverlay for overlay targeting. */
  readonly enemies: {
    getAllEnemies(): Iterable<unknown>;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface OracleConfig {
  /** Base awareness window duration in ms (5000ms). */
  readonly baseWindowMs: number;
  /** Medium cooldown tier in ms (1200ms). */
  readonly cooldownMs: number;
  /**
   * Phase Step base invulnerability in ms (250ms).
   * Used to restore the AstralTeleportSystem override when Oracle expires,
   * if oracle_phase_step had boosted it to 500ms.
   */
  readonly baseInvulnMs: number;
}

const DEFAULT_CONFIG: OracleConfig = {
  baseWindowMs: 5000,
  cooldownMs: 1200,  // medium cooldown tier
  baseInvulnMs: 250, // Phase Step base invuln — restored on Oracle deactivation
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class OracleSkillEffect extends ToggleSkillEffect {
  readonly skillId: string = 'oracle';

  protected readonly deps: OracleDeps;
  protected readonly config: OracleConfig;

  /** Remaining ms of the current awareness window. Counts down while active. */
  private _windowRemaining = 0;

  /** True until Phase Step consumes the free teleport this window. */
  private _freePhaseStepAvailable = false;

  /** Total number of successful activations. */
  private _activationsCount = 0;

  /** Resolved window duration (base + oracle_duration bonus), set on each activation. */
  private _resolvedWindowMs = 0;

  constructor(deps: OracleDeps, config: Partial<OracleConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get activationsCount(): number { return this._activationsCount; }
  get freePhaseStepAvailable(): boolean { return this._freePhaseStepAvailable; }
  get windowRemaining(): number { return this._windowRemaining; }
  get resolvedWindowMs(): number { return this._resolvedWindowMs; }

  // ---------------------------------------------------------------------------
  // ToggleSkillEffect implementation
  // ---------------------------------------------------------------------------

  canActivate(): boolean {
    return this.cooldownRemaining <= 0 && !this._isActive;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve window duration — base + oracle_duration additive bonus (scope: 'oracle').
    const durationBonus = this.deps.resolver.getAdditiveEffect('oracle', 'durationMs');
    this._resolvedWindowMs = this.config.baseWindowMs + durationBonus;
    this._windowRemaining = this._resolvedWindowMs;

    this._isActive = true;
    this._freePhaseStepAvailable = true;
    this._activationsCount++;

    // Show Oracle tactical overlay (patrol routes, aggro states, arc previews).
    this.deps.renderer.showOracleOverlay(this.deps.enemies.getAllEnemies());

    // Apply invuln override if oracle_phase_step is unlocked (scope: 'oracle').
    // This boosts Phase Step's invuln from 250ms to 500ms while Oracle is active.
    const invulnBonus = this.deps.resolver.getAdditiveEffect('oracle', 'phaseStepInvulnerabilityMs');
    if (invulnBonus > 0) {
      this.deps.teleport.setInvulnOverride(this.config.baseInvulnMs + invulnBonus);
    }

    return true;
  }

  /**
   * Consume the free Phase Step, if available.
   *
   * Called by PhaseStepSkillEffect.activate() before starting the cooldown.
   * Returns true and clears the flag if a free teleport is pending;
   * returns false if none is available (already used, or Oracle not active).
   */
  consumeFreePhaseStep(): boolean {
    if (!this._freePhaseStepAvailable) return false;
    this._freePhaseStepAvailable = false;
    return true;
  }

  protected override updateWhileActive(delta: number): void {
    this._windowRemaining -= delta;
    if (this._windowRemaining <= 0) {
      this._windowRemaining = 0;
      this.deactivate();
    }
  }

  protected override onDeactivate(): void {
    // Hide Oracle tactical overlay.
    this.deps.renderer.hideOracleOverlay();

    // Discard any unconsumed free Phase Step.
    this._freePhaseStepAvailable = false;

    // Restore Phase Step base invuln if oracle_phase_step had boosted it.
    const invulnBonus = this.deps.resolver.getAdditiveEffect('oracle', 'phaseStepInvulnerabilityMs');
    if (invulnBonus > 0) {
      this.deps.teleport.setInvulnOverride(this.config.baseInvulnMs);
    }

    // Start medium cooldown.
    this.cooldownRemaining = this.config.cooldownMs;
  }

  override destroy(): void {
    if (this._isActive) {
      this.deps.renderer.hideOracleOverlay();
      this._freePhaseStepAvailable = false;
      // Restore invuln if we boosted it.
      const invulnBonus = this.deps.resolver.getAdditiveEffect('oracle', 'phaseStepInvulnerabilityMs');
      if (invulnBonus > 0) {
        this.deps.teleport.setInvulnOverride(this.config.baseInvulnMs);
      }
    }
    super.destroy();
  }
}
